/**
 * Central WhatsApp routing + logging helper.
 *
 *  • `useServiceNumbers()` — react hook returning the 6 canonical platform
 *    WhatsApp numbers (plus the support/transportation legacy aliases), kept
 *    in localStorage so the gate decisions render correctly on guest first
 *    paint and offline.
 *  • `logAndOpen(...)` — POSTs to /api/whatsapp-activity then opens the wa.me
 *    URL in a new tab. Used by every WhatsApp button across the app.
 *  • Pure gating helpers:
 *      canShowPlatformService(service, numbers)
 *      canShowBusinessContact(business)
 *      canShowBusinessVIP(business)
 *      canShowVendorContact(vendor)
 */
import { useEffect, useState } from "react";
import { api } from "./api";
import { lsGet, lsSet } from "./safeStorage";
import { openWhatsApp, buildWaUrl as buildWa } from "./openWhatsApp";

const CACHE_KEY = "habano.wa.platform.v1";
let _anonId = null;
function anonId() {
  if (_anonId) return _anonId;
  _anonId = lsGet("habano.anon_id") || `guest-${Math.random().toString(36).slice(2, 11)}`;
  try { lsSet("habano.anon_id", _anonId); } catch { /* ignore */ }
  return _anonId;
}

export const SERVICE_KEY_TO_SETTING = {
  // platform services (in admin settings)
  ride: "whatsapp_general_ride",
  airport_ride: "whatsapp_airport_ride",
  vip_arrival: "whatsapp_vip_arrival",
  vip_departure: "whatsapp_vip_departure",
  tour_guide: "whatsapp_tour_guide",
  support: "whatsapp_support",
};

export function cleanPhone(s) {
  return (s || "").replace(/\D/g, "");
}

export function useServiceNumbers() {
  const [nums, setNums] = useState(() => {
    try { return JSON.parse(lsGet(CACHE_KEY) || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    api.get("/public-settings").then(({ data }) => {
      const next = {
        whatsapp_general_ride: data?.whatsapp_general_ride || data?.transportation_whatsapp || "",
        whatsapp_airport_ride: data?.whatsapp_airport_ride || "",
        whatsapp_vip_arrival: data?.whatsapp_vip_arrival || data?.concierge_whatsapp || "",
        whatsapp_vip_departure: data?.whatsapp_vip_departure || data?.concierge_whatsapp || "",
        whatsapp_tour_guide: data?.whatsapp_tour_guide || "",
        whatsapp_support: data?.whatsapp_support || "",
      };
      setNums(next);
      try { lsSet(CACHE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    }).catch(() => {});
  }, []);
  return nums;
}

/** Returns the configured platform number for the given service slug, or "" if none. */
export function platformNumberFor(service, nums) {
  const key = SERVICE_KEY_TO_SETTING[service];
  if (!key) return "";
  return cleanPhone(nums?.[key] || "");
}

export function canShowPlatformService(service, nums) {
  return !!platformNumberFor(service, nums);
}
export function canShowBusinessContact(business) {
  return !!cleanPhone(business?.whatsapp);
}
export function canShowBusinessVIP(business) {
  return !!business?.vip_enabled && !!cleanPhone(business?.whatsapp);
}
export function canShowVendorContact(vendor) {
  return !!cleanPhone(vendor?.whatsapp);
}

/** Build a wa.me URL with the encoded message. Delegates to the canonical
 *  openWhatsApp helper so every caller uses the same validation. */
export function buildWaUrl(number, message) {
  return buildWa(number, message);
}

/**
 * Log a WhatsApp transfer attempt, then open the wa.me URL.
 *
 * iter139 — Opens WhatsApp FIRST, synchronously, then fires the logging
 * POST as a side effect. This keeps the user-gesture stack intact so
 * iOS Safari and Chrome on Android don't block the popup.
 *
 *  service_type   one of SERVICE_TYPES on the backend
 *  number         already-resolved phone (platform or business/vendor)
 *  message        pre-filled body
 *  meta           {business, vendor, destination, anchor_at, language}
 */
export async function logAndOpen({ service_type, number, message, meta = {} }) {
  // 1. Open WhatsApp first — must be inside the same user-gesture tick.
  const url = openWhatsApp(number, message);
  // 2. Fire-and-forget log.
  try {
    api.post("/whatsapp-activity", {
      service_type,
      related_business_id: meta.business?.id || null,
      related_business_name: meta.business?.name || null,
      related_vendor_id: meta.vendor?.id || meta.vendor?.user_id || null,
      related_vendor_name: meta.vendor?.full_name || null,
      destination: meta.destination || null,
      full_message: message || "",
      language: meta.language || "en",
      anchor_at: meta.anchor_at || null,
      anon_id: anonId(),
    }).catch(() => {});
  } catch { /* ignore */ }
  return url;
}
